import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { Field, Form, FormSpy } from 'react-final-form';
import { Box, FormHelperText, Grid2 } from '@mui/material';
import ActionButtons from '../ActionButtons';
import Title from 'modules/Pricing/components/Title';
import { DataGrid, TextInput } from '@utriper/core-ui';

const AddRangeForm = ({ priceGroupId, handleClose = () => {} }) => {
    const { t } = useTranslation();
    const translate = (key) => t(`price_group_id.add_range_form.${key}`);
    const { addRange, startColumn, submittingPriceGroup, unitsPriceGroup, prices, validatePrices } = usePriceGroup();
    const lastQuantityTo = useMemo(() => prices?.sort((a, b) => a?.quantityTo - b?.quantityTo)?.[prices?.length - 1]?.quantityTo, [prices]);
    const [submitted, setSubmitted] = useState(false);
    const [priceRanges, setPriceRanges] = useState([]);
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);

    const sanitizeNumberString = (str) => {
        if (str === undefined || str === null) return '';
        const cleaned = String(str).replace(/[^0-9]/g, '');
        return cleaned === '' ? '' : String(parseInt(cleaned, 10));
    };

    const getHeaderRow = () => {
        const style = { justifyContent: 'flex-start', px: 2, fontSize: '14px', fontWeight: 'bold' };
        const unitsRows = unitsPriceGroup.map((unit) => ({ type: 'header', text: unit?.name, style }));

        const headerRow = [
            {
                rowId: 'header',
                cells: [{ type: 'header', text: translate('header_row'), style }, ...unitsRows],
                height: 45,
            },
        ];
        return headerRow;
    };

    const getRows = (values) => {
        const style = { justifyContent: 'flex-start', px: 2, fontSize: '14px', fontWeight: 400 };
        const nonEditable = true;

        return [
            ...getHeaderRow(),
            ...values.map(({ quantityFrom, quantityTo, prices }, index) => {
                let quantityLabel = '';
                if (quantityFrom === quantityTo) {
                    quantityLabel = String(quantityFrom);
                }
                if (!Number.isInteger(quantityTo)) {
                    quantityLabel = `${quantityFrom}+`;
                }
                if (Number.isInteger(quantityTo) && quantityFrom !== quantityTo) {
                    quantityLabel = `${quantityFrom} ${translate('to')} ${quantityTo}`;
                }

                return {
                    rowId: index,
                    cells: [
                        { type: 'text', text: quantityLabel, style, nonEditable },
                        ...prices.map(({ price = null, unitId }) => ({
                            type: 'number',
                            value: !Number.isNaN(price) ? price : null,
                            unitId,
                            nonEditable: false,
                        })),
                    ],
                };
            }),
        ];
    };

    useEffect(() => {
        if (Array.isArray(unitsPriceGroup) && unitsPriceGroup.length > 0) {
            const unitColumns = [];

            // Load units columns
            for (const unit of unitsPriceGroup) {
                if (!unit?.id) continue;

                const isDuplicated = unitColumns.some((col) => col?.columnId === unit.id);
                if (!isDuplicated) {
                    unitColumns.push({ columnId: unit.id, width: 190 });
                }
            }

            setColumns([startColumn, ...unitColumns]);
        }
    }, [unitsPriceGroup, startColumn]);

    useEffect(() => {
        if (Array.isArray(priceRanges) && priceRanges.length > 0) {
            const newRows = getRows(priceRanges);
            setRows([...newRows]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceRanges]);

    const handleChangePrices = (changes = [], prevDetails = []) => {
        for (const { rowId, type, newCell } of changes) {
            if (type !== 'number') continue;

            const dataRow = prevDetails?.find((d) => d?.rowId === rowId);
            if (!dataRow?.prices) continue;

            const { value, unitId } = newCell;
            if (!Number.isFinite(value)) continue;

            const priceToEdit = dataRow?.prices?.find((price) => price?.unitId === unitId);
            if (!priceToEdit) continue;

            priceToEdit.price = value;
        }
    };

    const handleChangeDataGrid = (changes) => {
        setPriceRanges((prevPrices) => {
            const updatePrices = [...prevPrices];
            handleChangePrices(changes, updatePrices);

            return updatePrices;
        });
    };

    const handleChangeForm = ({ values }) => {
        const { quantityFrom, quantityTo } = values;
        if (!Number.isFinite(quantityFrom)) return;

        const unitPrices = unitsPriceGroup.map((unitPriceGroup) => ({ unitId: unitPriceGroup?.id, price: null }));

        const rowId = 0;
        const priceRange = { rowId, quantityFrom, quantityTo, prices: [...unitPrices] };

        setPriceRanges((prevPrices) => {
            const prevRange = prevPrices.find((price) => price?.rowId === rowId);

            if (!prevRange) {
                return [priceRange];
            }

            const rangeUpdated = { ...prevRange, quantityFrom, quantityTo };

            return [rangeUpdated];
        });
    };

    const handleSubmit = async () => {
        await addRange(priceGroupId, priceRanges);
        handleClose();
    };

    const validateForm = ({ quantityFrom, quantityTo }) => {
        const errors = {};

        if (!Number.isFinite(quantityFrom)) {
            errors.quantityFrom = translate('validate_form.required');
        }

        if (quantityFrom !== lastQuantityTo + 1) {
            errors.quantityFrom = `${translate('validate_form.quantity_from')} ${lastQuantityTo + 1}!`;
        }

        if (quantityTo && !Number.isFinite(quantityTo)) {
            errors.quantityTo = translate('validate_form.invalid_quantity_to');
        }

        if (quantityTo && quantityTo <= lastQuantityTo) {
            errors.quantityTo = `${translate('validate_form.minor_quantity_to')} ${lastQuantityTo}!`;
        }

        const priceErrors = validatePrices(priceRanges, unitsPriceGroup);

        errors.priceRanges = priceErrors;

        return errors;
    };

    return (
        <Form
            key={`price-group-form-units-${priceGroupId}`}
            onSubmit={handleSubmit}
            validate={validateForm}
            render={({ errors, handleSubmit, form }) => {
                const hasPriceRangesErrors = submitted && errors?.priceRanges;
                const priceRangesErrors = errors?.priceRanges ?? [];

                return (
                    <form key={`price-group-form-units-${priceGroupId}`} onSubmit={handleSubmit} noValidate>
                        <Box component="section" py={2}>
                            <Title title={translate('title')} description={translate('description')} />

                            {/* Form */}
                            <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                <Grid2 container size={12} spacing={1} alignItems="center">
                                    {/* Quantity From */}
                                    <Grid2 size={6}>
                                        <Field name={'quantityFrom'}>
                                            {({ input, meta }) => (
                                                <TextInput
                                                    label={translate('form.quantity_from')}
                                                    meta={meta}
                                                    value={input?.value}
                                                    onChange={(event) => {
                                                        const cleaned = sanitizeNumberString(event?.target?.value);
                                                        event.target.value = cleaned;
                                                        input.onChange(cleaned === '' ? '' : Number(cleaned));
                                                    }}
                                                    input={{
                                                        fullWidth: true,
                                                        type: 'text',
                                                        inputMode: 'numeric',
                                                        slotProps: {
                                                            inputProps: { inputMode: 'numeric', pattern: '[0-9]*', min: 1 },
                                                        },
                                                        error: meta?.touched && Boolean(meta?.error),
                                                        helperText: meta?.touched && meta?.error ? meta.error : '',
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </Grid2>

                                    {/* Quantity To */}
                                    <Grid2 size={6}>
                                        <Field name={'quantityTo'}>
                                            {({ input, meta }) => (
                                                <TextInput
                                                    label={translate('form.quantity_to')}
                                                    meta={meta}
                                                    value={input?.value}
                                                    onChange={(event) => {
                                                        const raw = event?.target?.value;
                                                        if (raw === '') {
                                                            input.onChange('');
                                                            return;
                                                        }
                                                        const cleaned = sanitizeNumberString(raw);
                                                        event.target.value = cleaned;
                                                        input.onChange(cleaned === '' ? '' : Number(cleaned));
                                                    }}
                                                    input={{
                                                        fullWidth: true,
                                                        type: 'text',
                                                        inputMode: 'numeric',
                                                        slotProps: {
                                                            inputProps: { inputMode: 'numeric', pattern: '[0-9]*', min: 1 },
                                                        },
                                                        error: meta?.touched && Boolean(meta?.error),
                                                        helperText: meta?.touched && meta?.error ? meta.error : '',
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </Grid2>
                                </Grid2>

                                <Box width="100%" maxHeight="640px" overflow="auto" mt={1}>
                                    <DataGrid rows={rows} columns={columns} handleChange={handleChangeDataGrid} />
                                </Box>

                                {/* Errors Ranges */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxHeight: '78px',
                                        overflowY: 'auto',
                                        mt: 2,
                                    }}
                                >
                                    {hasPriceRangesErrors && priceRangesErrors?.length > 0 && priceRangesErrors.map((error) => <FormHelperText error={hasPriceRangesErrors}>{error}</FormHelperText>)}
                                </Box>
                            </Grid2>

                            <ActionButtons
                                handleSubmit={(e) => {
                                    setSubmitted(true);
                                    return handleSubmit(e);
                                }}
                                handleClose={(e) => {
                                    handleClose();
                                    return form.reset(e);
                                }}
                                submitting={submittingPriceGroup}
                                labelNext={translate('add_button')}
                            />
                        </Box>

                        {/* Form Spy */}
                        <FormSpy subscription={{ values: true }} onChange={handleChangeForm} />
                    </form>
                );
            }}
        />
    );
};

export default AddRangeForm;
